import { useState } from "react";

import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  TextField,
  Button,
  Banner,
} from "@shopify/polaris";

import { useTranslation } from "../hooks/useTranslation";

interface VipCodeCardProps {
  /** Called with the redeemed code; resolves/throws like the underlying API call. */
  onRedeem: (code: string) => Promise<{
    success: boolean;
    message?: string;
    error?: string;
    data?: { submissions_granted: number; promo_total: number };
  }>;
  /** Called after a successful redemption with the new total, so parents can refresh quota. */
  onRedeemed?: (promoTotal: number) => void;
}

/**
 * "You are a VIP!" card — settings-page entry point for redeeming a one-time VIP/promo token
 * that grants extra free submissions on top of the trial allowance (e.g. 7 -> 37).
 *
 * Deliberately shows nothing until a code is applied: the confirmation banner appears once,
 * right after a successful redemption, and disappears when dismissed or on the next visit.
 * The running bonus total lives on the home page / quota display, not repeated here.
 */
export function VipCodeCard({ onRedeem, onRedeemed }: VipCodeCardProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<{
    status: "success" | "critical";
    content: string;
  } | null>(null);

  const handleApply = async () => {
    if (!code.trim() || applying) return;
    setApplying(true);
    setResult(null);
    try {
      const response = await onRedeem(code.trim());
      if (response && response.success && response.data) {
        setResult({
          status: "success",
          content: t("settings.vip.success", {
            count: response.data.submissions_granted,
            total: response.data.promo_total,
          }),
        });
        setCode("");
        onRedeemed?.(response.data.promo_total);
      } else {
        setResult({
          status: "critical",
          content:
            (response && (response.error || response.message)) ||
            t("settings.notifications.error"),
        });
      }
    } catch (error) {
      console.error("[VipCodeCard] Error redeeming VIP code:", error);
      setResult({
        status: "critical",
        content: t("settings.notifications.error"),
      });
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card>
      <BlockStack gap="400">
        <BlockStack gap="200">
          <Text variant="headingMd" as="h2">
            {t("settings.vip.title")}
          </Text>
          <Text variant="bodyMd" as="p" tone="subdued">
            {t("settings.vip.subtitle")}
          </Text>
        </BlockStack>

        {result && (
          <Banner
            tone={result.status}
            title={
              result.status === "success"
                ? t("settings.vip.successTitle")
                : undefined
            }
            onDismiss={() => setResult(null)}
          >
            <p>{result.content}</p>
          </Banner>
        )}

        <InlineStack gap="300" blockAlign="end" wrap={false}>
          <div style={{ flexGrow: 1 }}>
            <TextField
              label={t("settings.vip.codeLabel")}
              labelHidden
              value={code}
              onChange={setCode}
              autoComplete="off"
              placeholder={t("settings.vip.codePlaceholder")}
              disabled={applying}
            />
          </div>
          <Button
            variant="primary"
            tone="magic"
            onClick={handleApply}
            loading={applying}
            disabled={!code.trim()}
          >
            {applying ? t("settings.vip.applying") : t("settings.vip.apply")}
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}
